// ABOUTME: SPEC-53 — the static collection carousel (Figma 403:656): one chrome-free composite image,
// ABOUTME: five transparent tap zones and five live labels in the reel rail's own type, plus a header.
//
// Everything on screen comes from the `enter_collection` payload — title, price header, image path,
// slot labels — so nothing here knows which collection it is showing. The composite is rendered
// `object-fit: cover` (client override 2026-09-14: the screen is always fully covered, cropping
// whichever axis overruns, never letterboxed) — `pickDesktopVariant` (stageGeometry.js) first picks
// whichever of three authored crops (16:9 / ~2.25:1 wide / ~4:3 tall) is closest to the window's own
// aspect, so the cover-crop on top of that is normally small; mobile swaps to an entirely different
// composition (the grid). The zones and labels are laid out as fractions of the cover box, so they
// land on the watch regardless of how much of the image is actually visible. On portrait viewports
// the labels reflow into a legible row instead of shrinking to 4px.
//
// Label type and spacing are the Vesper rail's `VariantPlate` (CollectionOverlay.js) at the
// frame's own sizes — headline 39 / subline 29 (`403:820`) where the reel rail is 55 / 42 — with
// the same cap-trim compensation. Copied, not imported: CollectionOverlay gates on RAIL_CHAPTERS and
// the player state and returns null on mobile (review note), none of which applies here.

import React, { useEffect, useState } from 'react';
import { useViewportSize, useBreakpoint } from '../Projectionist/useViewportSize';
import { useFadePresence } from '../Projectionist/overlays/useFadePresence';
import { TAG_FADE_MS, TAG_STAGGER_MS } from '../Projectionist/overlays/PointTagLayer';
import { CAP_HEIGHT_RATIO, capTrimmedPadding } from '../Projectionist/overlays/PointTag';
import {
  fillBox, slotFractions, labelLayout, resolveStageImage, MIN_TAP_PX, MIN_SUBLINE_PX,
  pickDesktopVariant, variantImagePath, DESKTOP_VARIANTS,
  pickMobileVariant, mobileImageVariant, gridSlotFractions, GRID_VARIANTS,
} from './stageGeometry';
import { MOBILE_FRAME, TAG_ROW_BOTTOM } from '../ReelChrome/mobileGeometry';
import { useControlsLift, useLiftTransition } from '../../uiExperiment/bottomChromeStore'; // SPEC-82 §4.7

// The grid header's own bottom gutter, as a fraction of the frame — same physical anchor the
// point-tag row uses (TAG_ROW_BOTTOM: one gutter above the CC bar, in the identical 1216x2160
// authored frame `GRID_DESIGN` shares with `MOBILE_FRAME`). Expressed as a fraction of the
// RENDERED box rather than re-deriving per `GRID_VARIANTS` design height, so it lands just above
// the bar identically on the default/short/tall crops without threading a second number through
// each one. The extra 100 clears the row by a full gutter's worth more than the point-tag row
// itself needs (owner feedback 2026-09-15: sitting exactly on the tag row's own line read as too
// tight against the CC bar).
const GRID_HEADER_EXTRA_GAP = 100;
const GRID_HEADER_BOTTOM_PCT = ((TAG_ROW_BOTTOM + GRID_HEADER_EXTRA_GAP) / MOBILE_FRAME.height) * 100;

const PLATE_LEADING = 1.24;
const PLATE_PAD = 53.33;
const PLATE_HEADLINE = 39;
const PLATE_SUBLINE = 29;
const halfExcess = (font) => (font * (PLATE_LEADING - CAP_HEIGHT_RATIO)) / 2;

/** Exported for the tests, which derive expectations from the authored type rather than re-typing it. */
export const STAGE_PLATE = {
  headline: PLATE_HEADLINE,
  subline: PLATE_SUBLINE,
  pad: PLATE_PAD,
  padTop: capTrimmedPadding(PLATE_PAD, PLATE_HEADLINE, PLATE_LEADING),
  padBottom: capTrimmedPadding(PLATE_PAD, PLATE_SUBLINE, PLATE_LEADING),
  gap: Math.max(0, 25 - halfExcess(PLATE_HEADLINE) - halfExcess(PLATE_SUBLINE)),
  radius: 37.33,
  leading: PLATE_LEADING,
};

/** Header `403:817` — the reel pill's treatment (`369:62`): ExtraBold uppercase title, Medium price. */
export const STAGE_HEADER = { font: 55.149, priceFont: 42, gap: 80, rowGap: 16, leading: 1.242, color: '#d9d9d9' };
const HEADER_MIN_FONT = 14;
const HEADER_MIN_PRICE = 11;

const slotName = (slot) => slot?.title
  || [slot?.headline, slot?.subline].filter(Boolean).join(' ')
  || String(slot?.id || '');

function SlotLabel({ slot, sizeScale, opacity, delayMs, onSelect, position }) {
  const s = sizeScale;
  return (
    // Presentational twin of the tap zone (the zone is the accessible control; this is its caption).
    // Still clickable, as the Vesper plates are, so a guest who reaches for the word is not refused.
    <div
      role="presentation"
      data-testid="stage-label"
      onClick={() => onSelect?.(slot)}
      className="flex flex-col items-center justify-center hover:bg-white/10 active:bg-white/15 text-reel-chrome-textPrimary text-center font-reel-medium"
      style={{
        ...position,
        padding: `${STAGE_PLATE.padTop * s}px ${STAGE_PLATE.pad * s}px ${STAGE_PLATE.padBottom * s}px`,
        borderRadius: `${STAGE_PLATE.radius * s}px`,
        gap: `${STAGE_PLATE.gap * s}px`,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        opacity,
        transition: `opacity ${TAG_FADE_MS}ms ease ${delayMs}ms, background-color 150ms ease`,
        pointerEvents: opacity ? 'auto' : 'none',
      }}
    >
      <span className="block" style={{ fontSize: `${STAGE_PLATE.headline * s}px`, lineHeight: STAGE_PLATE.leading }}>
        {slot.headline || slotName(slot)}
      </span>
      {slot.subline && (
        <span className="block" style={{ fontSize: `${STAGE_PLATE.subline * s}px`, lineHeight: STAGE_PLATE.leading }}>
          {slot.subline}
        </span>
      )}
    </div>
  );
}

function StageHeader({ title, priceHeader, sizeScale, floor, opacity, position }) {
  const font = floor ? Math.max(HEADER_MIN_FONT, STAGE_HEADER.font * sizeScale) : STAGE_HEADER.font * sizeScale;
  const priceFont = floor ? Math.max(HEADER_MIN_PRICE, STAGE_HEADER.priceFont * sizeScale) : STAGE_HEADER.priceFont * sizeScale;
  if (!title && !priceHeader) return null;
  return (
    <div
      data-testid="stage-header"
      className="flex flex-wrap items-baseline justify-center"
      style={{
        ...position,
        color: STAGE_HEADER.color,
        columnGap: `${STAGE_HEADER.gap * sizeScale}px`,
        rowGap: `${STAGE_HEADER.rowGap * sizeScale}px`,
        lineHeight: STAGE_HEADER.leading,
        opacity,
        // SPEC-82 review L3: the lift tween rides in `position.transition`; it composes with the fade, never replaced by it.
        transition: position.transition ? `opacity ${TAG_FADE_MS}ms ease, ${position.transition}` : `opacity ${TAG_FADE_MS}ms ease`,
        pointerEvents: 'none',
        whiteSpace: 'nowrap',
      }}
    >
      {title && <span className="font-reel-heading uppercase" style={{ fontSize: `${font}px` }}>{title}</span>}
      {priceHeader && <span className="font-reel-medium" style={{ fontSize: `${priceFont}px` }}>{priceHeader}</span>}
    </div>
  );
}

/** The screen is always fully covered on any window shape — no letterbox, ever (client override
 * 2026-09-14). Sized to `box` (from `fillBox`) rather than 100%/100% + `object-fit`, because past
 * the crop-safety floor `box`'s width/height come from independently-clamped axis scales: a plain
 * CSS `object-fit` can crop (`cover`) or letterbox (`contain`) but can't stretch one axis
 * independently of the other, which is exactly what keeps every watch visible once cropping alone
 * would push one off-screen. No `objectFit` set — an `<img>` sized away from its intrinsic ratio
 * stretches to fill by default, which is what `box` intends whenever its axes disagree. */
function StageImages({ src, box }) {
  if (!src) return null;
  return (
    // maxWidth/maxHeight: 'none' — Tailwind's preflight sets `img { max-width: 100% }`, which
    // silently clamps `box.width` back down to the container's own width whenever cropping (or the
    // crop-safety floor's stretch) needs the image bigger than the viewport. Without this override
    // the image renders at `min(box.width, 100%)` — undetectable from the inline style, only visible
    // as a black gap where the image should have overflowed and been clipped.
    <img src={src} alt="" draggable={false} data-testid="stage-composite"
         style={{
           position: 'absolute', left: box.left, top: box.top, width: box.width, height: box.height,
           maxWidth: 'none', maxHeight: 'none', userSelect: 'none',
         }} />
  );
}

/** The mobile grid (Figma 452:4043, `grid.jpg`) — five watches in a staggered pentagon, an entirely
 * different composition from the desktop row, not a crop of it. It never reflows: five fixed,
 * non-overlapping slot positions already have their own per-slot label anchor, so there is no shared
 * row to collide into. Only the type size is floored, the same legibility floor the desktop row
 * reflows to avoid. */
function GridStage({ collection, slots, box, src, mobileVariant, opacity, onSelect }) {
  const lift = useControlsLift('mobile'); // SPEC-82 control_reveal: the header clears the revealed row (0 when closed)
  const liftTransition = useLiftTransition('bottom');
  const zones = gridSlotFractions(mobileVariant).slice(0, slots.length);
  const s = Math.max(box.scale, MIN_SUBLINE_PX / STAGE_PLATE.subline);
  // `GRID_HEADER.y` (stageGeometry.js) put the title/price near the TOP of the frame — fine against
  // the bare grid artwork, but it now lands on top of the reel chrome that sits over this stage: the
  // ViewToggle "Views"/"<reel>" pills (top-left) and, on Ophion's `alwaysExpanded` icon rail
  // (top-right), all three cart/wishlist/privacy chips (reported 2026-09-15). Bottom-anchoring
  // instead, just above the CC bar the same way the point-tag row does, puts it in the one band nothing
  // else claims on this stage.
  const headerPosition = {
    position: 'absolute', left: '50%', bottom: `${GRID_HEADER_BOTTOM_PCT + (lift / MOBILE_FRAME.height) * 100}%`, transform: 'translateX(-50%)',
    ...(liftTransition ? { transition: liftTransition } : {}),
  };
  return (
    <div data-testid="collection-stage" data-layout="grid" style={{ position: 'absolute', inset: 0, overflow: 'hidden', background: '#000' }}>
      <StageImages src={src} box={box} />
      <div data-testid="stage-frame" style={{ position: 'absolute', left: box.left, top: box.top, width: box.width, height: box.height }}>
        <StageHeader title={collection?.title} priceHeader={collection?.price_header}
                     sizeScale={s} floor={false} opacity={opacity} position={headerPosition} />
        {zones.map((z, i) => (
          <button
            key={slots[i].id || i}
            type="button"
            data-testid="stage-slot"
            data-slot-id={slots[i].id}
            aria-label={slotName(slots[i])}
            onClick={() => onSelect?.(slots[i])}
            className="hover:bg-white/5 active:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white/70 transition-colors"
            style={{
              position: 'absolute', left: `${z.left * 100}%`, top: `${z.top * 100}%`,
              width: `${z.width * 100}%`, height: `${z.height * 100}%`,
              minWidth: MIN_TAP_PX, minHeight: MIN_TAP_PX,
              background: 'transparent', border: 'none', padding: 0, borderRadius: 12, cursor: 'pointer',
            }}
          />
        ))}
        {slots.map((slot, i) => (
          <SlotLabel
            key={slot.id || i}
            slot={slot}
            sizeScale={s}
            opacity={opacity}
            delayMs={i * TAG_STAGGER_MS}
            onSelect={onSelect}
            position={{ position: 'absolute', left: `${zones[i].centerX * 100}%`, top: `${zones[i].labelTop * 100}%`, transform: 'translateX(-50%)' }}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * @param {{ collection: object|null, onSelect?: (slot: object) => void }} props
 *   `collection` is the `enter_collection` descriptor: {key, title, image, price_header, slots[]}.
 */
export default function CollectionStage({ collection, onSelect }) {
  const { width, height } = useViewportSize();
  const isMobile = useBreakpoint() === 'mobile';
  const deskVariantKey = pickDesktopVariant(width, height);
  const deskVariant = DESKTOP_VARIANTS[deskVariantKey];
  const mobileVariantKey = pickMobileVariant(width, height);
  const mobileVariant = GRID_VARIANTS[mobileVariantKey];
  const design = isMobile ? mobileVariant.design : deskVariant.design;
  const watchBoxes = isMobile
    ? mobileVariant.slots
    : deskVariant.slotX.map((x) => ({ x, y: deskVariant.watch.y, w: deskVariant.watch.w, h: deskVariant.watch.h }));
  const box = fillBox(width, height, design, watchBoxes);
  const slots = Array.isArray(collection?.slots) ? collection.slots : [];

  const src = resolveStageImage(
    variantImagePath(collection?.image, isMobile ? mobileImageVariant(mobileVariantKey) : deskVariantKey),
  );

  // Mount at 0 and ease to 1 on the reel tag's clock (useFadePresence waits two frames so the
  // browser has an opacity-0 paint to ease FROM). A restore (transition "none") fades in too — the
  // owner's brief has the tags fading like the Vesper's on both sides of the black.
  const [armed, setArmed] = useState(false);
  useEffect(() => { setArmed(true); }, []);
  const { opacity } = useFadePresence(armed, TAG_FADE_MS);

  if (isMobile) {
    return (
      <GridStage collection={collection} slots={slots} box={box} src={src}
                 mobileVariant={mobileVariant} opacity={opacity} onSelect={onSelect} />
    );
  }

  const zones = slotFractions(slots.length, deskVariant);
  const layout = labelLayout(box, height, STAGE_PLATE.subline, { labelY: deskVariant.labelY, design: deskVariant.design });
  const inline = layout.inline;
  const s = layout.sizeScale;

  const headerPosition = inline || !layout.banded
    ? { position: 'absolute', left: '50%', top: `${(deskVariant.header.y / deskVariant.design.height) * 100}%`, transform: 'translateX(-50%)' }
    : { position: 'absolute', left: 0, right: 0, bottom: `${Math.max(0, height - box.top) + 8}px` };
  const headerInFrame = inline || !layout.banded;

  const labels = slots.map((slot, i) => (
    <SlotLabel
      key={slot.id || i}
      slot={slot}
      sizeScale={s}
      opacity={opacity}
      delayMs={i * TAG_STAGGER_MS}
      onSelect={onSelect}
      position={inline
        ? { position: 'absolute', left: `${zones[i].centerX * 100}%`, top: `${(deskVariant.labelY / deskVariant.design.height) * 100}%`, transform: 'translateX(-50%)' }
        : {}}
    />
  ));

  return (
    <div
      data-testid="collection-stage"
      data-layout={inline ? 'inline' : 'reflow'}
      style={{ position: 'absolute', inset: 0, overflow: 'hidden', background: '#000' }}
    >
      <StageImages src={src} box={box} />

      {/* The design frame: the cover box (can overrun the viewport and go negative — the parent's
          `overflow: hidden` clips it), so every child is a percentage of the active variant's design size. */}
      <div
        data-testid="stage-frame"
        style={{ position: 'absolute', left: box.left, top: box.top, width: box.width, height: box.height }}
      >
        {headerInFrame && (
          <StageHeader title={collection?.title} priceHeader={collection?.price_header}
                       sizeScale={s} floor={!inline} opacity={opacity} position={headerPosition} />
        )}
        {zones.map((z, i) => (
          <button
            key={slots[i].id || i}
            type="button"
            data-testid="stage-slot"
            data-slot-id={slots[i].id}
            aria-label={slotName(slots[i])}
            onClick={() => onSelect?.(slots[i])}
            className="hover:bg-white/5 active:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white/70 transition-colors"
            style={{
              position: 'absolute',
              left: `${z.left * 100}%`,
              top: `${z.top * 100}%`,
              width: `${z.width * 100}%`,
              height: `${z.height * 100}%`,
              minWidth: MIN_TAP_PX,
              minHeight: MIN_TAP_PX,
              background: 'transparent',
              border: 'none',
              padding: 0,
              borderRadius: 12,
              cursor: 'pointer',
            }}
          />
        ))}
        {inline && labels}
      </div>

      {!headerInFrame && (
        <StageHeader title={collection?.title} priceHeader={collection?.price_header}
                     sizeScale={s} floor opacity={opacity} position={headerPosition} />
      )}
      {!inline && (
        <div
          data-testid="stage-label-row"
          className="flex flex-wrap items-end justify-evenly"
          style={{ position: 'absolute', left: 0, right: 0, top: layout.rowTop, rowGap: 4, padding: '0 4px' }}
        >
          {labels}
        </div>
      )}
    </div>
  );
}
